import { useRequest } from 'ahooks';
import { useAppDispatch } from 'app/hooks';
import { JobCancellationStatusEnum, JobItem } from 'domains/workspaceDomain';
import { useEffect } from 'react';
import { JOB_ADJUSTMENT_WORKSPACE_FILTER } from 'utils/constants';
import { useRouteMatchFilter } from './useRouteMatchFilter';

/**
 * Starts a job cancellation and opens the cancellation modal.
 *
 * Shared so the Actions menu entry and the card-level Cancel Job link Canva orgs get are literally
 * the same operation: create the cancellation record when the job has none yet, remember which
 * workspace filter to come back to, then hand off to the modal.
 *
 * @param onOpen called once the cancellation record exists, to show the modal
 * @param withGlobalLoading whether this hook drives the full-page spinner. Pass false where the
 * caller already aggregates several loading flags into it -- the flag is a plain boolean, so a
 * second owner would clear the spinner while the first request is still running.
 */
export const useJobCancelJob = (
  data: JobItem,
  onOpen: () => void,
  withGlobalLoading = true
) => {
  const { rfm, app } = useAppDispatch();
  const { defaultFilter } = useRouteMatchFilter();

  const cancelJobFn = async () => {
    localStorage.setItem(JOB_ADJUSTMENT_WORKSPACE_FILTER, defaultFilter);

    if (data.cancellationStatus === JobCancellationStatusEnum.NONE) {
      await rfm.createJobCancellations(`${data.id}`);
      // the modal reads the record back, so give the create a moment to land
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    onOpen();
  };

  const { run: cancelJob, loading } = useRequest(cancelJobFn, { manual: true });

  useEffect(() => {
    if (!withGlobalLoading) {
      return;
    }
    if (loading) {
      app.showLoading();
    } else {
      app.hideLoading();
    }
  }, [app, loading, withGlobalLoading]);

  return { cancelJob, loading };
};
